import { FC, useMemo } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  CardContent,
  CircularProgress,
  Divider,
  Grid2 as Grid,
  Typography,
} from "@mui/material";
import { CardOutline } from "components/Card";
import { LearnersFeedbackTable } from "./LearnersFeedbackTable";
import { LearnerPerformanceChart } from "./LearnerPerformanceChart";
import {
  useGetFeedbacksByOrganizationIdQuery,
  useGetLearnersByOrgIdQuery,
  useGetOrgRequirementsByOrgQuery,
  useGetUsersRoleByOrgQuery,
} from "graphql/__generated__/hooks";
import { useAppSelector } from "hooks/useAppSelector";
import { organizationSelectedSelector } from "reduxStore/organization/selectors";
import { FeedbackType } from "types/feedback";
import { Requirement } from "types/requirement";
import { ROLES, User } from "types/user";
import { processLearnersAnalytics } from "../learnersAnalytics";

export const LearnersFeedback: FC = () => {
  const { organizationId } = useParams();
  const organization = useAppSelector(organizationSelectedSelector);
  const orgId = organization?.org_id || organizationId || "";

  const { data: learnersData, loading: loadingLearners } =
    useGetLearnersByOrgIdQuery({
      variables: { orgId },
      skip: !orgId,
    });

  const { data: studentMembersData, loading: loadingMembers } =
    useGetUsersRoleByOrgQuery({
      variables: { orgId, role: ROLES.STUDENT },
      skip: !orgId,
    });

  const { data: requirementsData, loading: loadingRequirements } =
    useGetOrgRequirementsByOrgQuery({
      variables: { orgId },
      skip: !orgId,
    });

  const { data: feedbackData, loading: loadingFeedbacks } =
    useGetFeedbacksByOrganizationIdQuery({
      variables: { organizationId: orgId },
      skip: !orgId,
    });

  const learners = useMemo((): User[] => {
    const byId = new Map<string, User>();

    for (const gc of learnersData?.graduation_class ?? []) {
      for (const row of gc.students ?? []) {
        const student = row.student;
        if (!student?.user_id) continue;
        byId.set(student.user_id, student as User);
      }
    }

    for (const membership of studentMembersData?.organization_user ?? []) {
      const student = membership.user;
      if (!student?.user_id) continue;
      const existing = byId.get(student.user_id);
      byId.set(student.user_id, {
        tags: [],
        ...(existing || {}),
        ...student,
      } as User);
    }

    return [...byId.values()];
  }, [learnersData, studentMembersData]);

  const requirements = useMemo((): Requirement[] => {
    return (requirementsData?.organization_requirement ?? [])
      .map((row) => row.requirement as Requirement | null | undefined)
      .filter((requirement): requirement is Requirement =>
        Boolean(requirement?.requirement_id),
      );
  }, [requirementsData]);

  const feedbacks = useMemo(
    () => (feedbackData?.feedback || []) as FeedbackType[],
    [feedbackData],
  );

  const processedLearners = useMemo(
    () =>
      processLearnersAnalytics(
        learners,
        requirements,
        feedbacks,
      ),
    [learners, requirements, feedbacks],
  );

  const stats = useMemo(() => {
    const totalLearners = processedLearners.length;
    const onTrack = processedLearners.filter(
      (learner) => learner.behindCount === 0 && learner.requirements.length > 0,
    ).length;
    const behind = processedLearners.filter(
      (learner) => learner.behindCount > 0,
    ).length;
    const notStarted = processedLearners.filter(
      (learner) =>
        learner.totalFeedbacks === 0 && learner.requirements.length > 0,
    ).length;
    return { totalLearners, onTrack, behind, notStarted };
  }, [processedLearners]);

  const loading =
    loadingLearners ||
    loadingMembers ||
    loadingRequirements ||
    loadingFeedbacks;

  if (!orgId) {
    return (
      <Typography variant="body2" color="text.secondary">
        Select an organization to view learner analytics.
      </Typography>
    );
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Grid container spacing={2}>
      <Grid size={{ lg: 4, xs: 12 }}>
        <CardOutline sx={{ height: "100%" }}>
          <CardContent>
            <Typography variant="h5" gutterBottom>
              Learner stats
            </Typography>
            <Divider sx={{ margin: "1rem 0" }} />
            <Grid container spacing={1}>
              <Grid size={6}>
                <Typography variant="body1" gutterBottom>
                  <strong>Learners:</strong>
                </Typography>
                <Typography variant="body1" gutterBottom>
                  <strong>Fully on track:</strong>
                </Typography>
                <Typography variant="body1" gutterBottom>
                  <strong>Behind:</strong>
                </Typography>
                <Typography variant="body1">
                  <strong>No feedback yet:</strong>
                </Typography>
              </Grid>
              <Grid size={6}>
                <Typography variant="body1" gutterBottom>
                  {stats.totalLearners}
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {stats.onTrack}
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {stats.behind}
                </Typography>
                <Typography variant="body1">{stats.notStarted}</Typography>
              </Grid>
            </Grid>
          </CardContent>
        </CardOutline>
      </Grid>
      <Grid size={{ lg: 12, xs: 12 }}>
        <CardOutline sx={{ height: "100%" }}>
          <CardContent>
            <LearnerPerformanceChart
              learners={learners}
              requirements={requirements}
              feedbacks={feedbacks}
            />
          </CardContent>
        </CardOutline>
      </Grid>
      <Grid size={{ lg: 12, xs: 12 }}>
        <CardOutline sx={{ height: "100%" }}>
          <CardContent>
            <Typography variant="h5" gutterBottom>
              Learners
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Compare each learner&apos;s Minimum Attempts and Target Score On
              Rubric with completed feedback attempts and coach ratings.
            </Typography>
            <Divider sx={{ margin: "1rem 0" }} />
            <LearnersFeedbackTable learners={processedLearners} />
          </CardContent>
        </CardOutline>
      </Grid>
    </Grid>
  );
};
